import { useMutation, useQueryClient } from "@tanstack/react-query";

import useConnectCalendar from "@app/analytics/hooks/calendar/useConnectCalendar";
import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import apiClient from "@app/common/api/apiClient";
import { INTEGRATION_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import useApps from "@app/common/hooks/useApps";
import type {
  CalendarProviderId,
  IntegrationProviderId,
  IntegrationScope,
  MeetingPlatform,
} from "@app/common/types";
import {
  isResponseErrorWithStatus,
  withQueryParamsUrl,
} from "@app/common/utils";
import { getIntegrationConnectUrl } from "@app/common/utils/providerUtils";
import { configureZoomSdk } from "@app/embedded/utils";
import openAppBrowser from "@app/embedded/zoom/utils/openAppBrowser";

interface AuthorizeIntegrationOpts {
  providerId: IntegrationProviderId;
  returnRoute: string;
  scope?: IntegrationScope;
  // optional params for connect url
  connectParams?: { [key: string]: string };
}

const initiateIntegrationAuth = async (
  client: MeetingPlatform,
  opts: Parameters<typeof getIntegrationConnectUrl>[0]
) => {
  const url = getIntegrationConnectUrl(opts);

  if (client === "zoom") {
    await configureZoomSdk();
    await openAppBrowser(url);
  } else {
    window.location.href = url;
  }
};

const useIntegration = () => {
  const { client } = useApps();
  const queryClient = useQueryClient();
  const { connectCalendar } = useConnectCalendar();
  const { getConnectUrl, getDisconnectUrl } = useIntegrations();

  const invalidateIntegrations = () => {
    queryClient.invalidateQueries({
      // invalidate all integration-related queries
      queryKey: ["integrations"],
    });
  };

  const enableIntegrationMutation = useMutation({
    mutationFn: apiClient.integrations.enableIntegration,
    onSuccess: invalidateIntegrations,
    onError: (error) => {
      // callers of this method should have separate handling for a 403 from this endpoint
      if (isResponseErrorWithStatus(error, 403)) return;
      invalidateIntegrations();
    },
  });

  // a successful connection will cause a page refresh since the user will
  // be taken through an OAuth flow, so no query invalidation needed
  const authorizeIntegrationMutation = useMutation({
    mutationFn: (opts: AuthorizeIntegrationOpts) => {
      const providerConfig = INTEGRATION_PROVIDERS_BY_ID[opts.providerId];

      if (providerConfig.kind === "calendar") {
        return connectCalendar(opts.providerId as CalendarProviderId, {
          action: providerConfig.connectAction,
          errorAction: providerConfig.connectAction,
          returnTo: opts.returnRoute,
          errorReturnTo: opts.returnRoute,
          queryParams: opts.connectParams,
        });
      }

      const connectUrl = getConnectUrl({ providerId: opts.providerId });

      if (!connectUrl) {
        throw new Error(
          `Invalid connection. Tried to connect ${opts.providerId} without a connect url`
        );
      }

      const providerRequiresScope = providerConfig.supportedScopes.length > 1;

      if (providerRequiresScope && !opts.scope) {
        throw new Error(
          `Ambiguous connect attempt. Tried to connect ${opts.providerId} without a scope`
        );
      }

      return initiateIntegrationAuth(client, {
        action: providerConfig.connectAction || "",
        route: withQueryParamsUrl(connectUrl, opts.connectParams ?? {}),
        returnRoute: opts.returnRoute,
        scope: providerRequiresScope ? opts.scope : undefined,
      });
    },
  });

  const disconnectIntegrationMutation = useMutation({
    mutationFn: (providerId: IntegrationProviderId) =>
      apiClient.integrations.disconnectIntegration(
        getDisconnectUrl({ providerId })
      ),
    onSettled: invalidateIntegrations,
  });

  const syncIntegrationMutation = useMutation({
    mutationFn: apiClient.integrations.postSyncIntegration,
  });

  return {
    authorize: authorizeIntegrationMutation,
    disconnect: disconnectIntegrationMutation,
    enable: enableIntegrationMutation,
    sync: syncIntegrationMutation,
  };
};

export default useIntegration;
